import type ArrayProxy from '@ember/array/proxy';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { endOfDay, startOfDay } from 'date-fns';
import { format, formatInTimeZone } from 'date-fns-tz';
import type AbilitiesService from 'ember-can/services/abilities';
import { keepLatestTask, task } from 'ember-concurrency';
import type { PaginatedRecordArray } from 'garaje/infinity-models/v3-offset';
import type AnnouncementModel from 'garaje/models/announcement';
import type TakeoverModel from 'garaje/models/takeover';
import type CurrentLocationService from 'garaje/services/current-location';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import { DATE_FNS_YYYY_MM_DD, modifyDateInTimeZone, parseYyyyMmDdInTimeZone } from 'garaje/utils/date-fns-tz-utilities';
import { reads } from 'macro-decorators';

import { fetchAnnouncementById, getAnnouncementQueryParams } from './announcement-queries';
import {
  fetchIncidentsPage,
  type IncidentAnnouncementData,
  type IncidentData,
  type IncidentSummary,
  type IncidentsPageResult,
  type IncidentUserData,
} from './incident-queries';
import { PAGE_SIZE } from './route-base';
import type { MessageLogListModel } from './route-base';

const DEFAULT_TIMEZONE = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || 'America/Los_Angeles';

export enum MessageLogColumn {
  Incident = 'Incident',
  ActivePeriod = 'Active period',
  CreatedBy = 'Created by',
  Recipients = 'Recipients',
  SentAt = 'Sent at',
  Message = 'Message',
  Location = 'Location',
}

export interface MessageLogEntry {
  id: string;
  sentAt: string;
  sender: string;
  senderId: string;
  senderProfilePictureUrl?: string;
  messageTitle: string;
  messageBody: string;
  location?: string;
  category?: string;
  employeeCount: number;
  visitorCount: number;
  markedAsSafeCount: number;
  incidentStatus: 'active' | 'ended' | null;
  activePeriod: string | null;
}

export default class CommunicationsMessageLogListControllerBase extends Controller {
  declare model: MessageLogListModel;
  @service declare store: Store;
  @service declare state: StateService;
  @service declare router: RouterService;
  @service declare currentLocation: CurrentLocationService;
  @service declare abilities: AbilitiesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare metrics: MetricsService;

  @tracked selectedLocationIds: string[] = [];
  @tracked incidentsByAnnouncementId: Map<string, IncidentSummary> = new Map();

  @tracked takeovers: ArrayProxy<TakeoverModel>[] = [];
  @tracked messageLogEntries: MessageLogEntry[] = [];
  @tracked totalCount = 0;
  @tracked currentPage = 1;
  @tracked pageSize = PAGE_SIZE;

  @tracked startDate: Date = new Date();
  @tracked endDate: Date = new Date();
  @tracked showCalendar = false;
  @tracked isActiveInLastSixMonths = false;

  @reads('currentLocation.timezone', DEFAULT_TIMEZONE) timezone!: string;

  get isIncidentMode(): boolean {
    return this.model.mode === 'incidents';
  }

  // message log currently loads slow; this way we have some info present before initializeData finishes
  hydrateFromModel(model: MessageLogListModel): void {
    this.messageLogEntries = [];
    this.takeovers = [];
    this.currentPage = 1;
    this.incidentsByAnnouncementId = new Map();
    this.isActiveInLastSixMonths = false;
    this.selectedLocationIds = [];

    if (model.mode === 'incidents') {
      this.totalCount = model.incidentsResult.totalCount;
    } else {
      this.totalCount = model.announcements?.meta?.total || 0;
    }
  }

  initializeData = task({ drop: true }, async (startDate: Date, endDate: Date, fetchCurrentLocation: boolean) => {
    this.startDate = startDate;
    this.endDate = endDate;
    this.selectedLocationIds = fetchCurrentLocation ? [this.currentLocation.location.id] : [];
    this.currentPage = 1;

    if (this.model.mode === 'incidents') {
      await this.initializeFromIncidents.perform(this.model.incidentsResult);
    } else {
      await this.initializeFromAnnouncements.perform();
    }
  });

  private initializeFromIncidents = task(async (result: IncidentsPageResult) => {
    this.messageLogEntries = this.buildEntriesFromIncidents(result);
    this.totalCount = result.totalCount;
    this.isActiveInLastSixMonths = this.totalCount > 0;

    await this.loadTakeoversFromEmberData.perform();
  });

  private initializeFromAnnouncements = task(async () => {
    if (this.model.mode !== 'announcements') return;

    this.incidentsByAnnouncementId = this.model.incidentsByAnnouncementId ?? new Map();
    const loadedIds = new Set(this.model.announcements.map((a) => a.id));
    // extraAnnouncements have createdAt before the range, so sort by incidentStart to place them correctly
    const extraAnnouncements = await this.fetchMissingAnnouncements(loadedIds, this.startDate, this.endDate);
    const sortedAnnouncements = [
      ...(this.model.announcements as unknown as AnnouncementModel[]),
      ...extraAnnouncements,
    ].sort((a, b) => {
      // eslint-disable-next-line ember/use-ember-get-and-set
      const aIncident = this.incidentsByAnnouncementId.get(a.id);
      // eslint-disable-next-line ember/use-ember-get-and-set
      const bIncident = this.incidentsByAnnouncementId.get(b.id);
      const aTime = aIncident ? new Date(aIncident.startedAt).getTime() : a.createdAt.getTime();
      const bTime = bIncident ? new Date(bIncident.startedAt).getTime() : b.createdAt.getTime();
      return bTime - aTime || b.createdAt.getTime() - a.createdAt.getTime();
    });
    const allEntries = await Promise.all(
      sortedAnnouncements.map((announcement) => this.createMessageLogEntry(announcement)),
    );
    this.messageLogEntries = this.deduplicateByIncident(allEntries);
    this.totalCount = this.model.announcements?.meta?.total || 0;
    this.isActiveInLastSixMonths = this.totalCount > 0;

    this.takeovers =
      (await Promise.all(this.model.announcements.map(async (announcement) => await announcement.takeovers))).flat() ||
      [];
  });

  refreshData = task({ drop: true }, async () => {
    if (this.isIncidentMode) {
      await this.refreshFromIncidents.perform();
    } else {
      await this.refreshFromAnnouncements.perform();
    }
  });

  protected refreshFromIncidents = task(async () => {
    const locationId = this.currentLocation.location.id;
    try {
      const result = await fetchIncidentsPage(locationId, this.startDate, this.endDate);
      this.messageLogEntries = this.buildEntriesFromIncidents(result);
      this.totalCount = result.totalCount;

      await this.loadTakeoversFromEmberData.perform();
    } catch (error) {
      this.metrics.logMonitorError({ event: 'message_log_refresh_incidents_failed', error });
    }
  });

  private refreshFromAnnouncements = task(async () => {
    const queryParams = getAnnouncementQueryParams(this.selectedLocationIds, this.startDate, this.endDate);
    const announcements = (await this.store.query(
      'announcement',
      queryParams,
    )) as unknown as PaginatedRecordArray<AnnouncementModel>;
    this.incidentsByAnnouncementId = new Map();
    const loadedAnnouncements = announcements as unknown as AnnouncementModel[];
    const loadedIds = new Set(loadedAnnouncements.map((a) => a.id));
    const extraAnnouncements = await this.fetchMissingAnnouncements(loadedIds, this.startDate, this.endDate);
    const sortedAnnouncements = [...loadedAnnouncements, ...extraAnnouncements].sort((a, b) => {
      // eslint-disable-next-line ember/use-ember-get-and-set
      const aIncident = this.incidentsByAnnouncementId.get(a.id);
      // eslint-disable-next-line ember/use-ember-get-and-set
      const bIncident = this.incidentsByAnnouncementId.get(b.id);
      const aTime = aIncident ? new Date(aIncident.startedAt).getTime() : a.createdAt.getTime();
      const bTime = bIncident ? new Date(bIncident.startedAt).getTime() : b.createdAt.getTime();
      return bTime - aTime || b.createdAt.getTime() - a.createdAt.getTime();
    });
    const allEntries = await Promise.all(
      sortedAnnouncements.map((announcement) => this.createMessageLogEntry(announcement)),
    );
    this.messageLogEntries = this.deduplicateByIncident(allEntries);
    this.totalCount = announcements?.meta?.total || this.messageLogEntries.length;
  });

  get messageLogHeaders(): readonly MessageLogColumn[] {
    if (this.featureFlags.isEnabled('emno-end-incident-web')) {
      return [
        MessageLogColumn.Incident,
        MessageLogColumn.ActivePeriod,
        MessageLogColumn.CreatedBy,
        MessageLogColumn.Recipients,
      ];
    }
    return [MessageLogColumn.SentAt, MessageLogColumn.Message, MessageLogColumn.Recipients];
  }

  get hasMorePages(): boolean {
    return this.pageSize * this.currentPage < this.totalCount;
  }

  @action
  setStartDate(date: Date): void {
    this.startDate = date;
  }

  @action
  setEndDate(date: Date): void {
    this.endDate = date;
  }

  get startDateFormat(): string {
    const { timezone, startDateWithDefault } = this;

    return formatInTimeZone(startDateWithDefault, timezone, 'MM/dd/yyyy');
  }

  get endDateFormat(): string {
    const { timezone, endDateWithDefault } = this;

    return formatInTimeZone(endDateWithDefault, timezone, 'MM/dd/yyyy');
  }

  get dateWithDefault(): string {
    return formatInTimeZone(new Date(), this.timezone, DATE_FNS_YYYY_MM_DD);
  }

  get startDateWithDefault(): Date {
    if (this.startDate) return new Date(this.startDate);

    const { dateWithDefault } = this;

    const date = parseYyyyMmDdInTimeZone(dateWithDefault, new Date(), this.timezone);
    const start = modifyDateInTimeZone(date, this.timezone, startOfDay);
    return start;
  }

  get endDateWithDefault(): Date {
    if (this.endDate) return new Date(this.endDate);

    const { dateWithDefault } = this;
    const date = parseYyyyMmDdInTimeZone(dateWithDefault, new Date(), this.timezone);
    const end = modifyDateInTimeZone(date, this.timezone, endOfDay);

    return end;
  }

  loadMoreData = keepLatestTask(async () => {
    if (!this.hasMorePages) {
      return;
    }

    if (this.isIncidentMode) {
      await this.loadMoreFromIncidents.perform();
    } else {
      await this.loadMoreFromAnnouncements.perform();
    }
  });

  private loadMoreFromIncidents = task(async () => {
    const nextPage = this.currentPage + 1;
    const locationId = this.currentLocation.location.id;
    let result: IncidentsPageResult;
    try {
      result = await fetchIncidentsPage(locationId, this.startDate, this.endDate, this.pageSize, nextPage);
    } catch (error) {
      this.metrics.logMonitorError({ event: 'message_log_load_more_incidents_failed', error });
      return;
    }
    this.currentPage = nextPage;
    const newEntries = this.buildEntriesFromIncidents(result);
    this.messageLogEntries = this.deduplicateByIncident([...this.messageLogEntries, ...newEntries]);
  });

  private loadMoreFromAnnouncements = task(async () => {
    const offset = this.currentPage * this.pageSize;
    this.currentPage++;
    const queryParams = getAnnouncementQueryParams(
      this.selectedLocationIds,
      this.startDate,
      this.endDate,
      this.pageSize,
      offset,
    );
    const newAnnouncements = await this.store.query('announcement', queryParams);

    if (Number(newAnnouncements.length) > 0) {
      const newMessagLogEntries = await Promise.all(
        newAnnouncements.map((announcement) => this.createMessageLogEntry(announcement)),
      );
      this.messageLogEntries = this.deduplicateByIncident([...this.messageLogEntries, ...newMessagLogEntries]);
    }
  });

  @action
  async onDatesChanged(startDate: Date, endDate: Date): Promise<void> {
    this.startDate = startDate;
    this.endDate = endDate;
    this.currentPage = 1;
    await this.refreshData.perform();
  }

  get totalMessages(): number {
    return this.messageLogEntries?.length ?? 0;
  }

  locationName = (locationId: string): string => {
    return this.store.peekRecord('skinny-location', locationId)?.name ?? '';
  };

  formatActivePeriod(startDate: Date, endDate: Date | null, timezone?: string): string {
    const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (!endDate) {
      // Active: "Feb 23, 2026 2:42 PM – Present"
      return `${formatInTimeZone(startDate, tz, 'MMM d, yyyy h:mm a')} – Present`;
    }

    const startYear = parseInt(formatInTimeZone(startDate, tz, 'yyyy'), 10);
    const endYear = parseInt(formatInTimeZone(endDate, tz, 'yyyy'), 10);
    const startDay = formatInTimeZone(startDate, tz, 'yyyy-MM-dd');
    const endDay = formatInTimeZone(endDate, tz, 'yyyy-MM-dd');
    const tzAbbr = formatInTimeZone(endDate, tz, 'zzz');

    if (startDay === endDay) {
      // Same day: "Feb 23, 2026 2:42 PM – 8:30 PM PST"
      const startStr = formatInTimeZone(startDate, tz, 'MMM d, yyyy h:mm a');
      const endStr = formatInTimeZone(endDate, tz, 'h:mm a');
      return `${startStr} – ${endStr} ${tzAbbr}`;
    }

    if (startYear === endYear) {
      // Multi-day same year: "Feb 23, 2:42 PM – Feb 24, 8:30 PM PST"
      const startStr = formatInTimeZone(startDate, tz, 'MMM d, h:mm a');
      const endStr = formatInTimeZone(endDate, tz, 'MMM d, h:mm a');
      return `${startStr} – ${endStr} ${tzAbbr}`;
    }

    // Multi-year: "Dec 31, 2025, 11:00 PM – Jan 1, 2026, 2:00 AM PST"
    const startStr = formatInTimeZone(startDate, tz, 'MMM d, yyyy, h:mm a');
    const endStr = formatInTimeZone(endDate, tz, 'MMM d, yyyy, h:mm a');
    return `${startStr} – ${endStr} ${tzAbbr}`;
  }

  formatDate(date: Date, timezone?: string): string {
    const dateFormat = "MMM dd, yyyy 'at' h:mm a zzz";

    if (!timezone) {
      return format(date, dateFormat);
    }

    return formatInTimeZone(date, timezone, dateFormat);
  }

  @action
  trackIncidentSelected(entry: MessageLogEntry): void {
    if (!this.featureFlags.isEnabled('emno-end-incident-web') || entry.incidentStatus === null) return;
    this.metrics.trackEvent('WEB_INCIDENT_SELECTED', {
      company_id: this.state.currentCompany?.id,
      location_id: this.currentLocation.location?.id,
    });
  }

  protected trackCreateIncidentTapped(): void {
    if (this.featureFlags.isEnabled('emno-end-incident-web')) {
      this.metrics.trackEvent('WEB_CREATE_NEW_INCIDENT_BUTTON_TAPPED', {
        company_id: this.state.currentCompany?.id,
        location_id: this.currentLocation.location?.id,
      });
    }
  }

  private deduplicateByIncident(entries: MessageLogEntry[]): MessageLogEntry[] {
    if (!this.featureFlags.isEnabled('emno-follow-up-web')) return entries;
    const incidentFirstEntry = new Map<string, MessageLogEntry>();
    return entries.filter((entry) => {
      const incident = this.incidentsByAnnouncementId.get(entry.id); // eslint-disable-line ember/use-ember-get-and-set
      if (!incident) return true;
      const existing = incidentFirstEntry.get(incident.id); // eslint-disable-line ember/use-ember-get-and-set
      if (existing) {
        existing.messageTitle = entry.messageTitle;
        existing.messageBody = entry.messageBody;
        existing.sender = entry.sender;
        existing.senderId = entry.senderId;
        existing.senderProfilePictureUrl = entry.senderProfilePictureUrl;
        return false;
      }
      incidentFirstEntry.set(incident.id, entry); // eslint-disable-line ember/use-ember-get-and-set
      return true;
    });
  }

  private async fetchMissingAnnouncements(
    loadedIds: Set<string>,
    startDate: Date,
    endDate: Date,
  ): Promise<AnnouncementModel[]> {
    const missingIds = [...this.incidentsByAnnouncementId.entries()]
      .filter(([, incident]) => {
        const incidentStart = new Date(incident.startedAt);
        const incidentEnd = incident.endedAt ? new Date(incident.endedAt) : null;
        return incidentStart <= endDate && (incidentEnd === null || incidentEnd >= startDate);
      })
      .filter(([id]) => !loadedIds.has(id))
      .map(([id]) => id);
    return Promise.all(missingIds.map((id) => fetchAnnouncementById(this.store, id)));
  }

  protected async createMessageLogEntry(announcement: AnnouncementModel): Promise<MessageLogEntry> {
    const creator = await announcement.creatorEmployee;
    const locationId = announcement.belongsTo('location').id();
    const locationName = this.locationName(locationId);
    const stats = announcement.statistics;
    // eslint-disable-next-line ember/use-ember-get-and-set
    const incidentCandidate = this.incidentsByAnnouncementId.get(announcement.id);
    const incidentStart = incidentCandidate ? new Date(incidentCandidate.startedAt) : null;
    const incidentEnd = incidentCandidate?.endedAt ? new Date(incidentCandidate.endedAt) : null;
    const incidentInRange =
      incidentStart !== null &&
      incidentStart <= this.endDateWithDefault &&
      (incidentEnd === null || incidentEnd >= this.startDateWithDefault);
    const incident = incidentInRange ? incidentCandidate : undefined;

    return {
      id: announcement.id,
      sentAt: this.formatDate(announcement.createdAt, this.state.currentLocation.timezone),
      sender: creator.name || 'Unknown',
      senderId: creator.id,
      senderProfilePictureUrl: creator.profilePictureUrl,
      messageTitle: announcement.title,
      messageBody: announcement.message,
      location: locationName || '',
      employeeCount: stats?.sent_employee_recipients ?? 0,
      visitorCount: stats?.visitor_recipients ?? 0,
      markedAsSafeCount: stats?.safe_recipients ?? 0,
      incidentStatus: incident?.status ?? null,
      activePeriod: incident
        ? this.formatActivePeriod(
            new Date(incident.startedAt),
            incident.endedAt ? new Date(incident.endedAt) : null,
            this.state.currentLocation.timezone,
          )
        : null,
    };
  }

  protected buildEntriesFromIncidents(result: IncidentsPageResult): MessageLogEntry[] {
    this.updateIncidentMap(result.incidents);
    const entries = this.buildEntriesFromIncidentData(result);
    return this.deduplicateByIncident(entries);
  }

  private updateIncidentMap(incidents: IncidentData[]): void {
    const incidentMap = new Map(this.incidentsByAnnouncementId);
    for (const incident of incidents) {
      const summary: IncidentSummary = {
        id: incident.id,
        status: incident.status,
        startedAt: incident.startedAt,
        endedAt: incident.endedAt,
      };
      for (const annId of incident.announcementIds) {
        // eslint-disable-next-line ember/use-ember-get-and-set
        incidentMap.set(annId, summary);
      }
    }
    this.incidentsByAnnouncementId = incidentMap;
  }

  private buildEntriesFromIncidentData(result: IncidentsPageResult): MessageLogEntry[] {
    const entries: MessageLogEntry[] = [];
    for (const incident of result.incidents) {
      for (const annId of incident.announcementIds) {
        // eslint-disable-next-line ember/use-ember-get-and-set
        const ann = result.announcements.get(annId);
        if (!ann) continue;
        // eslint-disable-next-line ember/use-ember-get-and-set
        const user = ann.creatorUserId ? result.users.get(ann.creatorUserId) : null;
        entries.push(this.createMessageLogEntryFromIncident(ann, incident, user ?? null));
      }
    }
    return entries;
  }

  private createMessageLogEntryFromIncident(
    announcement: IncidentAnnouncementData,
    incident: IncidentData,
    creator: IncidentUserData | null,
  ): MessageLogEntry {
    const timezone = this.state.currentLocation.timezone;
    return {
      id: announcement.id,
      sentAt: this.formatDate(new Date(announcement.createdAt), timezone),
      sender: creator?.name || 'Unknown',
      senderId: creator?.id || '',
      senderProfilePictureUrl: creator?.profilePictureUrl ?? undefined,
      messageTitle: announcement.title,
      messageBody: announcement.message,
      location: announcement.locationName || '',
      employeeCount: announcement.sentEmployeesCount,
      visitorCount: announcement.sentVisitorsCount,
      markedAsSafeCount: announcement.safeCount,
      incidentStatus: incident.status,
      activePeriod: this.formatActivePeriod(
        new Date(incident.startedAt),
        incident.endedAt ? new Date(incident.endedAt) : null,
        timezone,
      ),
    };
  }

  // Takeover data is not available on the incidents endpoint — still requires Ember Data announcement query.
  // TODO: include takeover data in the incidents response to eliminate this second network call.
  private loadTakeoversFromEmberData = task(async () => {
    const queryParams = getAnnouncementQueryParams(
      [this.currentLocation.location.id],
      this.startDate,
      this.endDate,
      PAGE_SIZE,
    );
    const announcements = await this.store.query('announcement', queryParams);
    this.takeovers =
      (await Promise.all(announcements.map(async (announcement) => await announcement.takeovers))).flat() || [];
  });
}
